<template>
  <div
    class="modal fade"
    id="editEventModal"
    tabindex="-1"
    aria-labelledby="editEventModalLabel"
    aria-hidden="true"
    ref="modalElement"
  >
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title fs-5 text-uppercase">
            Formulario de modificación de eventos
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <vee-form
            :validation-schema="formSchema"
            @submit="handleSubmit"
            @invalid-submit="onInvalidSubmit"
            ref="formRef"
          >
            <div class="card-body">
              <div class="form theme-form">
                <div class="row">
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="mb-3">
                      <label>
                        Título del evento <span class="text-danger">*</span>
                      </label>
                      <vee-field
                        class="form-control"
                        name="title"
                        type="text"
                        placeholder="Título del Evento"
                        v-model="formData.title"
                        oninput="this.value = this.value.toUpperCase()"
                      />
                      <ErrorMessage class="text-danger" name="title" />
                    </div>
                  </div>

                  <MultiOrientedSelector
                    :orienteds="orienteds"
                    field-name="oriented_ids"
                    v-model="formData.orientedJson"
                    @orienteds-changed="handleOrientedsChange"
                    ref="multiOrientedSelector"
                  />
                </div>

                <div class="row">
                  <div class="col-12">
                    <div class="mb-3">
                      <label>
                        Descripción del evento
                        <span class="text-danger">*</span>
                      </label>
                      <vee-field
                        class="form-control"
                        name="description"
                        type="text"
                        placeholder="Descripción del Evento"
                        v-model="formData.description"
                      />
                      <ErrorMessage class="text-danger" name="description" />
                    </div>
                  </div>
                </div>

                <DatesEvent
                  v-model="formData.dates"
                  @validation-change="handleDatesValidation"
                  ref="datesComponent"
                />

                <ScheduleEvent
                  v-if="formData.dates.duration > 0"
                  :duration="formData.dates.duration"
                  :initial-schedules="initialSchedulesJson"
                  ref="scheduleComponent"
                />

                <div class="row">
                  <EventTypeSelector
                    :kinds="kinds"
                    v-model="formData.kind_event_code"
                    @type-change="handleEventTypeChange"
                    ref="eventTypeSelector"
                  />

                  <PlaceSelector
                    v-if="showPlaceFields"
                    :places="places"
                    v-model="formData.place_code"
                    @capacity-change="handleCapacityChange"
                    ref="placeSelector"
                  >
                    <template #modal-create>
                      <ModalCreate @reload-list-place="loadPlaces" />
                    </template>
                  </PlaceSelector>
                </div>

                <CapacityControl
                  v-if="showPlaceFields"
                  v-model:minimum="formData.minimum"
                  v-model:maximum="formData.maximum"
                  v-model:withLimitControl="formData.withLimitControl"
                  :place-capacity="placeCapacity"
                  ref="capacityControl"
                />

                <div class="row">
                  <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                    <div class="mb-3">
                      <UploadField
                        id="banner"
                        name="banner"
                        label="Subir banner"
                        accept=".jpg,.jpeg,.png,.webp"
                        :required="false"
                        :existing-file="existingFiles.banner"
                        ref="bannerUpload"
                      />
                    </div>
                  </div>
                  <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                    <div class="mb-3">
                      <UploadField
                        id="poster"
                        name="poster"
                        label="Subir afiche"
                        accept=".jpg,.jpeg,.png,.webp"
                        :required="false"
                        :existing-file="existingFiles.poster"
                        ref="posterUpload"
                      />
                    </div>
                  </div>
                  <div class="col-xl-4 col-lg-4 col-md-12 col-sm-12">
                    <div class="mb-3">
                      <UploadField
                        id="regulation"
                        name="regulation"
                        label="Subir convocatoria"
                        accept=".pdf"
                        :required="false"
                        :existing-file="existingFiles.regulation"
                        ref="regulationUpload"
                      />
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6 col-12">
                    <div class="mb-3">
                      <label>Detalles, observaciones ó solicitudes</label>
                      <vee-field
                        as="textarea"
                        name="detail"
                        class="form-control"
                        placeholder="Aclarar si se requiere banner y afiche"
                        rows="3"
                        v-model="formData.detail"
                      ></vee-field>
                      <ErrorMessage class="text-danger" name="detail" />
                    </div>
                  </div>
                  <div class="col-md-6 col-12">
                    <FormSearch
                      :listEnrollment="listEnrollment"
                      field-name="enrollment"
                      v-model="formData.enrollment"
                      :required="true"
                      ref="enrollmentSelector"
                    />
                  </div>
                  <div class="col-12">
                    <UnicomRequests v-model="formData.unicomRequests" />
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary">
                Actualizar Evento
              </button>
            </div>
          </vee-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import ModalCreate from "@/pages/config/places/components/ModalCreate.vue";
import fileSender from "@/includes/fileconnection";
import UploadField from "@/pages/eventos/components/FormComponents/uploadFile.vue";
import DatesEvent from "@/pages/eventos/components/FormComponents/datesEvent.vue";
import ScheduleEvent from "@/pages/eventos/components/FormComponents/ScheduleEvent.vue";
import MultiOrientedSelector from "@/pages/eventos/components/FormComponents/MultipleOrientedSelect.vue";
import FormSearch from "@/pages/eventos/components/FormComponents/FormSearch.vue";
import UnicomRequests from "@/pages/eventos/components/FormComponents/UnicomRequests.vue";
import EventTypeSelector from "@/pages/eventos/components/FormComponents/typeEvent.vue";
import PlaceSelector from "@/pages/eventos/components/FormComponents/placeSelector.vue";
import CapacityControl from "@/pages/eventos/components/FormComponents/capacityEvent.vue";
import { Modal } from "bootstrap";

import { useEventForm } from "@/pages/eventos/components/FormComponents/servicesForm";
import { useEventValidation } from "@/pages/eventos/components/FormComponents/eventValidation";
import { useConfirmDialog } from "@/pages/eventos/components/FormComponents/statusDialogs";

const props = defineProps({
  eventData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["updated"]);

const {
  places,
  kinds,
  orienteds,
  listEnrollment,
  today,
  loadAllData,
  loadPlaces,
  getPlaceCapacity,
} = useEventForm();

const { validateAll } = useEventValidation();
const { confirmUpdate, successUpdate, errorUpdate, incompleteForm } =
  useConfirmDialog();

const modalInstance = ref(null);
const modalElement = ref(null);
const formRef = ref(null);

const multiOrientedSelector = ref(null);
const enrollmentSelector = ref(null);
const scheduleComponent = ref(null);
const datesComponent = ref(null);
const eventTypeSelector = ref(null);
const placeSelector = ref(null);
const capacityControl = ref(null);

const bannerUpload = ref(null);
const posterUpload = ref(null);
const regulationUpload = ref(null);

const formData = ref({
  title: "",
  description: "",
  dates: {
    init_date_inscription: "",
    end_date_inscription: "",
    init_date: "",
    end_date: "",
    duration_inscription: 0,
    duration: 0,
  },
  kind_event_code: "",
  place_code: "",
  minimum: "",
  maximum: "",
  withLimitControl: true,
  detail: "",
  enrollment: "",
  orientedJson: "",
  unicomRequests: {
    requireBanner: 0,
    requirePoster: 0,
    requireSocialMedia: 0,
    requirePrensa: 0,
    requireLiveStream: 0,
    isWebinar: 0,
    requirePhotoOrVideo: 0,
  },
});

const eventTypeData = ref({
  isVirtual: false,
  isPresencial: false,
  isHybrid: false,
});

const placeCapacity = ref(0);
const firstOrientedId = ref(null);
const datesValid = ref(false);
const initialSchedulesJson = ref("");
const existingFiles = ref({
  banner: null,
  poster: null,
  regulation: null,
});

const showPlaceFields = computed(() => !eventTypeData.value.isVirtual);

const formSchema = computed(() => {
  const isVirtual = eventTypeData.value.isVirtual;
  return {
    title: "required",
    description: "required",
    init_date: "required",
    end_date: "required",
    init_date_inscription: "required",
    end_date_inscription: "required",
    kind_event_code: "required",
    place_code: isVirtual ? "" : "required",
    minimum: isVirtual ? "" : "required|numeric|min_value:1",
    maximum: isVirtual ? "" : "required|numeric|min_value:1",
    detail: "required",
  };
});

const formatDateForInput = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const loadEventData = (eventData) => {
  if (!eventData || !eventData.id) return;

  formData.value.title = eventData.title || "";
  formData.value.description = eventData.description || "";
  formData.value.detail = eventData.detail || "";
  formData.value.kind_event_code = eventData.kind_event_code || "";
  formData.value.place_code = eventData.place_code || "";
  formData.value.minimum = eventData.minimum || "";
  formData.value.maximum = eventData.maximum || "";
  formData.value.withLimitControl = eventData.with_limit_control == 1;

  formData.value.dates = {
    init_date_inscription: formatDateForInput(eventData.init_date_inscription),
    end_date_inscription: formatDateForInput(eventData.end_date_inscription),
    init_date: formatDateForInput(eventData.init_date),
    end_date: formatDateForInput(eventData.end_date),
    duration_inscription: eventData.duration_inscription || 0,
    duration: eventData.duration || 0,
  };

  initialSchedulesJson.value = eventData.schedules_json || "";

  formData.value.enrollment = eventData.form?.enrollment_form_id || "";

  if (eventData.orienteds_json) {
    formData.value.orientedJson = eventData.orienteds_json;
    try {
      const parsed = JSON.parse(eventData.orienteds_json);
      if (parsed.orienteds && parsed.orienteds.length > 0) {
        firstOrientedId.value = parsed.orienteds[0].id;
      }
    } catch (error) {
      console.error("Error al cargar orientaciones:", error);
    }
  }

  if (eventData.unicom_requirements) {
    const req = eventData.unicom_requirements;
    formData.value.unicomRequests = {
      requireBanner: req.require_banner ? 1 : 0,
      requirePoster: req.require_poster ? 1 : 0,
      requireSocialMedia: req.require_social_media ? 1 : 0,
      requirePrensa: req.require_prensa ? 1 : 0,
      requireLiveStream: req.require_live_stream ? 1 : 0,
      isWebinar: req.is_webinnar ? 1 : 0,
      requirePhotoOrVideo: req.require_photo_video ? 1 : 0,
    };
  }

  if (eventData.storage && Array.isArray(eventData.storage)) {
    const baseServerUrl = process.env.VUE_APP_SERVER_SIDE_URL || "";

    eventData.storage.forEach((file) => {
      if (file.source === "banner") {
        existingFiles.value.banner = {
          name: file.extension || file.filename,
          url: `${baseServerUrl}/${file.filename}`,
        };
      } else if (file.source === "poster") {
        existingFiles.value.poster = {
          name: file.extension || file.filename,
          url: `${baseServerUrl}/${file.filename}`,
        };
      } else if (file.source === "regulation") {
        existingFiles.value.regulation = {
          name: file.extension || file.filename,
          url: `${baseServerUrl}/${file.filename}`,
        };
      }
    });
  }

  if (eventData.place_code) {
    placeCapacity.value = getPlaceCapacity(eventData.place_code);
  }
};

const handleOrientedsChange = (data) => {
  if (data.json) {
    try {
      const parsed = JSON.parse(data.json);
      if (parsed.orienteds && parsed.orienteds.length > 0) {
        firstOrientedId.value = parsed.orienteds[0].id;
      }
    } catch (error) {
      console.error("Error orienteds", error);
    }
  } else {
    firstOrientedId.value = null;
  }
};

const handleEventTypeChange = (typeData) => {
  eventTypeData.value = typeData;
};

const handleCapacityChange = (capacity) => {
  placeCapacity.value = capacity;
};

const handleDatesValidation = (isValid) => {
  datesValid.value = isValid;
};

const onInvalidSubmit = () => {
  incompleteForm();
};

const handleSubmit = async (values) => {
  await nextTick();

  const combinedValues = {
    ...values,
    ...formData.value.dates,
    kind_event_code: formData.value.kind_event_code,
    place_code: formData.value.place_code,
    minimum: formData.value.minimum,
    maximum: formData.value.maximum,
  };

  if (!datesValid.value || !datesComponent.value?.validate()) {
    return;
  }

  const isValid = await validateAll({
    formData: combinedValues,
    today: today.value,
    kinds: kinds.value,
    scheduleComponent,
    multiOrientedSelector,
    enrollmentSelector,
    withLimitControl: formData.value.withLimitControl,
    getPlaceCapacity,
  });

  if (!isValid) return;

  const result = await confirmUpdate();
  if (!result.isConfirmed) return;

  try {
    const schedulesJson = scheduleComponent.value?.getScheduleJson();
    const firstHour = scheduleComponent.value?.getFirstHour();

    const submitData = new FormData();

    submitData.append("title", combinedValues.title.toUpperCase());
    submitData.append("description", combinedValues.description);
    submitData.append("detail", combinedValues.detail);
    submitData.append("kind_event_code", combinedValues.kind_event_code);
    submitData.append(
      "init_date_inscription",
      combinedValues.init_date_inscription
    );
    submitData.append(
      "end_date_inscription",
      combinedValues.end_date_inscription
    );
    submitData.append("init_date", combinedValues.init_date);
    submitData.append("end_date", combinedValues.end_date);
    submitData.append(
      "duration_inscription",
      combinedValues.duration_inscription
    );
    submitData.append("duration", combinedValues.duration);
    submitData.append("oriented_id", firstOrientedId.value);
    submitData.append("orienteds_json", formData.value.orientedJson);
    submitData.append("schedules_json", schedulesJson);
    submitData.append("hour", firstHour);

    if (!eventTypeData.value.isVirtual) {
      if (combinedValues.place_code) {
        submitData.append("place_code", combinedValues.place_code);
      }
      submitData.append("minimum", combinedValues.minimum);
      submitData.append("maximum", combinedValues.maximum);
      submitData.append(
        "with_limit_control",
        formData.value.withLimitControl ? "1" : "0"
      );
    }

    Object.entries(formData.value.unicomRequests).forEach(([key, value]) => {
      const val = value === true || value === 1 ? 1 : 0;
      submitData.append(key, val.toString());
    });

    if (formData.value.enrollment) {
      submitData.append("enrollment_form_id", formData.value.enrollment);
    }

    const bannerFile = bannerUpload.value?.getCurrentFile();
    const posterFile = posterUpload.value?.getCurrentFile();
    const regulationFile = regulationUpload.value?.getCurrentFile();

    if (bannerFile && bannerFile instanceof File) {
      submitData.append("banner", bannerFile);
    }

    if (posterFile && posterFile instanceof File) {
      submitData.append("poster", posterFile);
    }

    if (regulationFile && regulationFile instanceof File) {
      submitData.append("regulation", regulationFile);
    }

    const response = await fileSender.put(
      `/event/${props.eventData.id}`,
      submitData
    );

    await successUpdate("El evento");
    emit("updated", response.data);
    closeModal();
  } catch (error) {
    console.error("Error al actualizar evento:", error);
    errorUpdate();
  }
};

const showModal = () => {
  if (modalInstance.value) {
    modalInstance.value.show();
    nextTick(() => {
      if (multiOrientedSelector.value && props.eventData?.orienteds_json) {
        multiOrientedSelector.value.loadExistingData(
          props.eventData.orienteds_json
        );
      }
    });
  }
};

const closeModal = () => {
  if (modalInstance.value) {
    modalInstance.value.hide();
  }
};

watch(modalElement, (el) => {
  if (el) {
    modalInstance.value = new Modal(el);
  }
});

watch(
  () => props.eventData,
  (newEventData) => {
    if (newEventData && newEventData.id) {
      loadEventData(newEventData);
    }
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  await loadAllData();
  if (props.eventData && props.eventData.id) {
    loadEventData(props.eventData);
  }
});

defineExpose({ showModal, closeModal });
</script>
